/**
 * Header Component
 * Role-based navigation: admin gets operations nav, pilots get flight nav
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { getUserFullName } from '@/types/auth';
import Button from './ui/Button';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { OPERATOR_NAME } from '@/config/branding';

interface NavItem {
  href: string;
  label: string;
}

const PILOT_NAV: NavItem[] = [
  { href: '/dashboard', label: 'Dashboard' },
  { href: '/trips', label: 'Trips' },
  { href: '/logbook', label: 'My Flights' },
  { href: '/mel/search', label: 'MEL' },
  { href: '/pay', label: 'Pay Summary' },
];

const ADMIN_NAV: NavItem[] = [
  { href: '/ops', label: 'Operations' },
  { href: '/approvals', label: 'Approvals' },
  { href: '/duty-time', label: 'Duty Time' },
  { href: '/admin?tab=users', label: 'Pilots' },
  { href: '/trips', label: 'Trips' },
  { href: '/admin?tab=aircraft', label: 'Fleet' },
  { href: '/safety-reports', label: 'Safety' },
  { href: '/mel/search', label: 'MEL' },
  { href: '/mel/documents', label: 'MEL Docs' },
  { href: '/expense-audit', label: 'Audit' },
  { href: '/logbook', label: 'My Flights' },
  { href: '/pay', label: 'My Pay' },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleTheme = () => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  };

  const isAdmin = user?.role === 'admin';
  const navItems = isAdmin ? ADMIN_NAV : PILOT_NAV;

  const isActive = (href: string) => {
    if (href.includes('?')) {
      return location.pathname + location.search === href;
    }
    return location.pathname === href;
  };

  const homePath = isAdmin ? '/ops' : '/dashboard';

  return (
    <header
      className="sticky top-0 z-50 border-b"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--border-default)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <h1
              className="text-xl font-bold cursor-pointer"
              style={{ color: 'var(--text-primary)' }}
              onClick={() => navigate(homePath)}
            >
              {OPERATOR_NAME}
            </h1>
            {isAdmin && (
              <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                ops
              </span>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.href)
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-muted'
                }`}
                style={isActive(item.href) ? undefined : { color: 'var(--text-secondary)' }}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md hover:bg-muted transition-colors"
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {mode === 'dark' ? (
                <SunIcon className="w-5 h-5 text-foreground/60" />
              ) : (
                <MoonIcon className="w-5 h-5 text-foreground/60" />
              )}
            </button>

            <div className="flex items-center gap-2 pl-3 border-l border-border">
              <div className="text-right">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user && getUserFullName(user)}</p>
                <p className="text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>{user?.role}</p>
              </div>
              <UserCircleIcon className="w-8 h-8" style={{ color: 'var(--text-secondary)' }} />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              icon={<ArrowRightOnRectangleIcon className="w-4 h-4" />}
              iconPosition="left"
            >
              Logout
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Toggle menu"
          >
            {showMobileMenu ? (
              <XMarkIcon className="w-6 h-6 text-foreground" />
            ) : (
              <Bars3Icon className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-border py-4">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href) ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                  }`}
                  style={isActive(item.href) ? undefined : { color: 'var(--text-primary)' }}
                >
                  {item.label}
                </a>
              ))}

              <div className="border-t border-border my-2" />

              <div className="px-3 py-2">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user && getUserFullName(user)}</p>
                <p className="text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>{user?.role}</p>
              </div>

              <button
                onClick={toggleTheme}
                className="px-3 py-2 text-sm font-medium rounded-md hover:bg-muted text-left flex items-center gap-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {mode === 'dark' ? (
                  <><SunIcon className="w-5 h-5" /> Light Mode</>
                ) : (
                  <><MoonIcon className="w-5 h-5" /> Dark Mode</>
                )}
              </button>

              <button
                onClick={handleLogout}
                className="px-3 py-2 text-sm font-medium rounded-md hover:bg-muted text-left flex items-center gap-2"
                style={{ color: 'var(--color-danger)' }}
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                Logout
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
